/**
 * Hierarchical TanStack Query keys for the Content Studio module.
 *
 * ESA factory pattern (lib/esa/query-keys.ts) — NO `mode`, NO `workspaceId`
 * in any key. The Live/Demo mode-keying was deleted from apps/web
 * ("Production is the only mode"); tenant isolation is enforced by
 * `WorkspaceCacheGuard` (queryClient.clear() on workspace/company switch) +
 * the `x-workspace-id` header the API client auto-attaches. Keys carry
 * resource + filters ONLY.
 *
 * Filters go INTO the key so distinct filter combos cache separately, and a
 * targeted `invalidateQueries({ queryKey: contentStudioKeys.decks.all() })`
 * refreshes just that namespace (never a global refetch). `lists()` is the
 * common prefix of every `list(filters)` key — invalidate it to refresh the
 * tables without refetching an open detail's heavy `deckJson` body.
 */

/**
 * A filter object folded into a query key. Structural (not a strict index
 * signature) so the module's typed filter interfaces (DeckListFilters,
 * PaletteListFilters) pass without a cast. TanStack hashes it structurally.
 * An omitted argument folds in as `{}` via the parameter default (not a
 * nullish fallback — this directory sits inside the components/ grep guard).
 */
type Filters = object

export const contentStudioKeys = {
  all: () => ['content-studio'] as const,

  ideas: {
    all: () => [...contentStudioKeys.all(), 'ideas'] as const,
    list: (filters: Filters = {}) => [...contentStudioKeys.ideas.all(), 'list', filters] as const,
  },

  /** GET policy-starters — the whole catalogue, filtered in the browser. */
  policyStarters: {
    all: () => [...contentStudioKeys.all(), 'policy-starters'] as const,
  },

  decks: {
    all: () => [...contentStudioKeys.all(), 'decks'] as const,
    /** Common prefix of every `list(filters)` key — invalidate lists only. */
    lists: () => [...contentStudioKeys.decks.all(), 'list'] as const,
    list: (filters: Filters = {}) =>
      [...contentStudioKeys.decks.lists(), filters] as const,
    detail: (id: string) =>
      [...contentStudioKeys.decks.all(), 'detail', id] as const,
    /** Generated card-image asset rows for one deck (Task 22). */
    assets: (id: string) =>
      [...contentStudioKeys.decks.all(), 'assets', id] as const,
    /** Per-locale translation coverage + live job state (parity wave 3). */
    translations: (id: string) =>
      [...contentStudioKeys.decks.all(), 'translations', id] as const,
    /**
     * One forged preview (PreviewFrame). Under `detail(id)`, so a save's
     * detail invalidation refreshes it; each theme and backdrop override is
     * cached apart, 'own' standing for the deck's own.
     */
    preview: (id: string, paletteKey = 'own', backdrop = 'own') =>
      [...contentStudioKeys.decks.detail(id), 'preview', paletteKey, backdrop] as const,
  },

  palettes: {
    all: () => [...contentStudioKeys.all(), 'palettes'] as const,
    lists: () => [...contentStudioKeys.palettes.all(), 'list'] as const,
    list: (filters: Filters = {}) =>
      [...contentStudioKeys.palettes.lists(), filters] as const,
  },

  /** AI generation sessions (Task 17 routes, consumed by the studio page). */
  sessions: {
    all: () => [...contentStudioKeys.all(), 'sessions'] as const,
    lists: () => [...contentStudioKeys.sessions.all(), 'list'] as const,
    list: (filters: Filters = {}) =>
      [...contentStudioKeys.sessions.lists(), filters] as const,
    detail: (id: string) =>
      [...contentStudioKeys.sessions.all(), 'detail', id] as const,
  },

  /** SCORM export ledger (Task 23 routes, consumed by the export dialog). */
  exports: {
    all: () => [...contentStudioKeys.all(), 'exports'] as const,
    forDeck: (deckId: string) =>
      [...contentStudioKeys.exports.all(), 'deck', deckId] as const,
  },

  /** Publication log (Task 24 route — the publish dialog's status poll). */
  publications: {
    all: () => [...contentStudioKeys.all(), 'publications'] as const,
    forDeck: (deckId: string) =>
      [...contentStudioKeys.publications.all(), 'deck', deckId] as const,
  },

  /**
   * Free Form courses (plan 2026-10-12, C6). Every per-course key is
   * `[...all, <resource>, id, …]`, so invalidating one resource never
   * refetches another — above all, a save never refetches the canvas
   * `preview` (the builder patches the running player over the bridge, and
   * a refetch would reload the iframe under the author's cursor). Filters
   * and params go last; invalidating with the bare `(id)` form matches every
   * filter combination (TanStack matches an object key partially).
   */
  freeForm: {
    all: () => [...contentStudioKeys.all(), 'free-form'] as const,
    /** Common prefix of every `list(filters)` key. */
    lists: () => [...contentStudioKeys.freeForm.all(), 'list'] as const,
    list: (filters: Filters = {}) => [...contentStudioKeys.freeForm.lists(), filters] as const,
    detail: (id: string) => [...contentStudioKeys.freeForm.all(), 'detail', id] as const,
    versions: (id: string) => [...contentStudioKeys.freeForm.all(), 'versions', id] as const,
    lint: (id: string) => [...contentStudioKeys.freeForm.all(), 'lint', id] as const,
    jobs: (id: string, filters: Filters = {}) => [...contentStudioKeys.freeForm.all(), 'jobs', id, filters] as const,
    exports: (id: string) => [...contentStudioKeys.freeForm.all(), 'exports', id] as const,
    publications: (id: string) => [...contentStudioKeys.freeForm.all(), 'publications', id] as const,
    /** The forged html of the canvas, the preview or the SCORM test page. */
    preview: (id: string, params: Filters = {}) => [...contentStudioKeys.freeForm.all(), 'preview', id, params] as const,
    pictures: (id: string, params: Filters = {}) => [...contentStudioKeys.freeForm.all(), 'pictures', id, params] as const,
    /** One course image as a data: URL, for the canvas (tinted for `paletteKey` when abstract). */
    mediaData: (id: string, key: string, paletteKey: string | null = null) =>
      [...contentStudioKeys.freeForm.all(), 'media-data', id, key, paletteKey] as const,
    estimate: (id: string, params: Filters = {}) => [...contentStudioKeys.freeForm.all(), 'estimate', id, params] as const,
    defaults: () => [...contentStudioKeys.freeForm.all(), 'defaults'] as const,
  },

  /** The size of each format's HumanFirewall library (Create training's "Browse 56 library decks"). */
  libraryTotal: (format: string) => [...contentStudioKeys.all(), 'library-total', format] as const,

  /**
   * Curated header-image library. Static product content with no tenant
   * state, so it is cached indefinitely; `suggest(topic)` keys separately per
   * card topic because the ordering differs per card.
   */
  headerImages: {
    all: () => [...contentStudioKeys.all(), 'header-images'] as const,
    library: (topic = '') =>
      [...contentStudioKeys.headerImages.all(), 'library', topic] as const,
    promptPresets: () =>
      [...contentStudioKeys.headerImages.all(), 'prompt-presets'] as const,
    /** Every picture in every style (static product content). */
    catalogue: () => [...contentStudioKeys.headerImages.all(), 'catalogue'] as const,
    /** The recommendation for one card of one training, for its current text. */
    recommended: (deckId: string, cardId: string, text: string, current: string | null) =>
      [...contentStudioKeys.headerImages.all(), 'recommended', deckId, cardId, text, current] as const,
  },
}
